<template>
  <div class="invite-shell">
    <div class="invite-orb invite-orb-one"></div>
    <div class="invite-orb invite-orb-two"></div>

    <div class="invite-card" data-testid="accept-invite-page">
      <img class="invite-logo" :src="brandLogo" alt="BeyondWebsite.ai" />

      <div v-if="state === 'loading'" class="invite-state">
        <div class="invite-spinner"></div>
        <h1>Checking your invite</h1>
        <p>We are validating the invitation link before you join the workspace.</p>
      </div>

      <div v-else-if="state === 'invalid'" class="invite-state">
        <div class="invite-icon error">!</div>
        <h1>Invite unavailable</h1>
        <p>{{ errorMessage }}</p>
        <router-link to="/login" class="invite-btn invite-btn-secondary">Back to login</router-link>
      </div>

      <div v-else-if="state === 'success'" class="invite-state">
        <div class="invite-icon success">✓</div>
        <h1>Welcome aboard</h1>
        <p>Your account is ready. Taking you to your websites...</p>
      </div>

      <template v-else>
        <p class="invite-kicker">Team invitation</p>
        <h1>Join {{ invite.tenantName || 'this workspace' }}</h1>
        <p class="invite-copy">
          Complete your profile for <strong>{{ invite.email }}</strong> to start building and maintaining websites with your team.
        </p>

        <form class="invite-form" data-testid="accept-invite-form" @submit.prevent="acceptInvite">
          <label class="invite-label" for="invite-name">Display name</label>
          <input
            id="invite-name"
            v-model="form.name"
            class="invite-input"
            type="text"
            minlength="2"
            maxlength="100"
            required
            :disabled="submitting"
            placeholder="Your name"
            data-testid="accept-invite-name"
          />

          <label class="invite-label" for="invite-password">Password</label>
          <input
            id="invite-password"
            v-model="form.password"
            class="invite-input"
            type="password"
            minlength="6"
            maxlength="128"
            required
            :disabled="submitting"
            placeholder="Choose a password"
            data-testid="accept-invite-password"
          />

          <p v-if="errorMessage" class="invite-alert" role="alert" data-testid="accept-invite-error">{{ errorMessage }}</p>

          <button class="invite-btn" type="submit" :disabled="submitting" data-testid="accept-invite-submit">
            {{ submitting ? 'Accepting invite...' : 'Accept invite' }}
          </button>
        </form>

        <p class="invite-footnote">
          This invite expires {{ formattedExpiry }}.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import api from '../utils/api'
import brandLogo from '../assets/bw-logo-wordmark.svg'

export default {
  name: 'AcceptInvite',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const sitesStore = useSitesStore()

    const state = ref('loading')
    const submitting = ref(false)
    const errorMessage = ref('')
    const invite = ref({})
    const form = reactive({ name: '', password: '' })

    const token = computed(() => String(route.query.token || '').trim())

    const formattedExpiry = computed(() => {
      if (!invite.value?.expiresAt) return 'soon'
      const date = new Date(invite.value.expiresAt)
      if (Number.isNaN(date.getTime())) return 'soon'
      return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    })

    const loadInvite = async () => {
      errorMessage.value = ''
      if (!token.value) {
        errorMessage.value = 'No invitation token was found in this link.'
        state.value = 'invalid'
        return
      }

      try {
        const response = await api.get(`/auth/invites/${encodeURIComponent(token.value)}`)
        invite.value = response.data || {}
        form.name = invite.value.name || ''
        state.value = 'ready'
      } catch (err) {
        errorMessage.value = err?.response?.data?.error || 'This invitation link is invalid, expired, or already used.'
        state.value = 'invalid'
      }
    }

    const acceptInvite = async () => {
      errorMessage.value = ''
      if (!form.name.trim()) {
        errorMessage.value = 'Enter your display name.'
        return
      }
      if (form.password.length < 6) {
        errorMessage.value = 'Password must be at least 6 characters.'
        return
      }

      submitting.value = true
      try {
        const response = await api.post('/auth/accept-invite', {
          token: token.value,
          name: form.name.trim(),
          password: form.password,
        })
        const data = response.data
        localStorage.setItem('bwai_token', data.token)
        localStorage.setItem('bwai_user', JSON.stringify(data.user))
        localStorage.setItem('bwai_tenant', JSON.stringify(data.tenant))
        api.defaults.headers.common.Authorization = `Bearer ${data.token}`
        sitesStore.setUser(data.user, data.tenant)
        state.value = 'success'
        setTimeout(() => router.push('/sites'), 900)
      } catch (err) {
        errorMessage.value = err?.response?.data?.error || 'Failed to accept invitation. Please try again.'
      } finally {
        submitting.value = false
      }
    }

    onMounted(loadInvite)

    return {
      brandLogo,
      state,
      invite,
      form,
      submitting,
      errorMessage,
      formattedExpiry,
      acceptInvite,
    }
  },
}
</script>

<style scoped>
.invite-shell {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 24px;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 18% 10%, rgba(124,109,250,0.26), transparent 34rem),
    radial-gradient(circle at 88% 82%, rgba(52,211,153,0.12), transparent 30rem),
    linear-gradient(135deg, #11111d 0%, #171426 52%, #0e1724 100%);
}

.invite-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: radial-gradient(circle at center, black 0%, transparent 72%);
}

.invite-orb {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
  filter: blur(12px);
}
.invite-orb-one { width: 260px; height: 260px; top: -80px; right: 14%; background: rgba(124,109,250,.2); }
.invite-orb-two { width: 220px; height: 220px; bottom: -70px; left: 10%; background: rgba(52,211,153,.13); }

.invite-card {
  position: relative;
  z-index: 1;
  width: min(460px, 100%);
  padding: 34px;
  border: var(--glass-border-hi);
  border-radius: 28px;
  background:
    linear-gradient(145deg, rgba(255,255,255,0.10), rgba(255,255,255,0.045)),
    rgba(22,22,38,.78);
  box-shadow: var(--elev-lg), inset 0 1px 0 rgba(255,255,255,0.08);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
}

.invite-logo {
  width: min(280px, 80vw);
  height: auto;
  display: block;
  margin-bottom: 22px;
}

.invite-kicker {
  margin: 0 0 10px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(28px, 5vw, 40px);
  line-height: 1;
  letter-spacing: -.055em;
  font-weight: 850;
}

.invite-copy,
.invite-state p,
.invite-footnote {
  color: var(--text-secondary);
  line-height: 1.65;
}

.invite-copy { margin: 14px 0 24px; font-size: 14px; }
.invite-copy strong { color: var(--text-primary); }

.invite-form {
  display: flex;
  flex-direction: column;
  gap: 11px;
}

.invite-label {
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.invite-input {
  width: 100%;
  padding: 12px 14px;
  border: var(--glass-border);
  border-radius: 14px;
  background: rgba(255,255,255,.07);
  color: var(--text-primary);
  font: inherit;
  outline: none;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.invite-input:focus {
  border: var(--glass-border-hi);
  background: rgba(255,255,255,.1);
  box-shadow: 0 0 0 4px rgba(124,109,250,.14);
}
.invite-input:disabled { opacity: .62; cursor: not-allowed; }

.invite-alert {
  margin: 2px 0 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(248,113,113,.28);
  background: rgba(248,113,113,.12);
  color: var(--danger);
  font-size: 13px;
}

.invite-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 42px;
  margin-top: 8px;
  padding: 10px 16px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--accent), #34d399);
  color: #fff;
  font: inherit;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(124,109,250,.25);
}
.invite-btn:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
.invite-btn-secondary { background: var(--glass-bg-active); border: var(--glass-border); box-shadow: none; }

.invite-footnote {
  margin: 18px 0 0;
  font-size: 12px;
}

.invite-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.invite-spinner {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  border: 3px solid rgba(124,109,250,.22);
  border-top-color: var(--accent);
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.invite-icon {
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  font-size: 24px;
  font-weight: 900;
}
.invite-icon.success { color: var(--success); background: rgba(52,211,153,.14); }
.invite-icon.error { color: var(--danger); background: rgba(248,113,113,.14); }

@media (max-width: 520px) {
  .invite-shell { padding: 16px; }
  .invite-card { padding: 26px; border-radius: 22px; }
}
</style>
